import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/integrations/supabase/client'
import { useAuth } from './useAuth'
import { useProfile } from './useProfile'
import { 
  format,
  subMonths,
  startOfMonth,
  endOfMonth,
  parseISO,
  eachMonthOfInterval,
  eachDayOfInterval,
  startOfWeek,
  endOfWeek,
  getDay,
  getHours
} from 'date-fns'
import { ptBR } from 'date-fns/locale'

interface ReportFilters {
  startDate: Date
  endDate: Date
  clientIds?: string[]
  projectIds?: string[]
  billingTypes?: ('hourly' | 'fixed')[]
  categories?: string[]
}

// Summary stats for reports - NOW USING PAYMENTS DATA FOR REVENUE
export function useReportsSummary(filters: ReportFilters) {
  const { user } = useAuth()
  const { data: profile } = useProfile()

  return useQuery({
    queryKey: ['reports', 'summary', user?.id, filters],
    queryFn: async () => {
      if (!user?.id) return null

      const startDateStr = format(filters.startDate, 'yyyy-MM-dd')
      const endDateStr = format(filters.endDate, 'yyyy-MM-dd')

      // Get PAID payments in date range (using paid_date for actual revenue)
      const { data: paidPayments } = await supabase
        .from('payments')
        .select(`
          amount,
          project_id,
          projects (
            id,
            billing_type,
            client_id,
            hourly_rate,
            fixed_price
          )
        `)
        .eq('status', 'paid')
        .gte('paid_date', startDateStr)
        .lte('paid_date', endDateStr)

      // Filter by project/client/billing type
      let filteredPayments = paidPayments || []
      
      if (filters.projectIds?.length) {
        filteredPayments = filteredPayments.filter((p: any) => 
          filters.projectIds?.includes(p.project_id)
        )
      }
      if (filters.clientIds?.length) {
        filteredPayments = filteredPayments.filter((p: any) =>
          filters.clientIds?.includes(p.projects?.client_id)
        )
      }
      if (filters.billingTypes?.length) {
        filteredPayments = filteredPayments.filter((p: any) =>
          filters.billingTypes?.includes(p.projects?.billing_type)
        )
      }

      // Calculate revenue from payments
      let hourlyRevenue = 0
      let fixedRevenue = 0

      filteredPayments.forEach((payment: any) => {
        const amount = Number(payment.amount) || 0
        if (payment.projects?.billing_type === 'hourly') {
          hourlyRevenue += amount
        } else {
          fixedRevenue += amount
        }
      })

      // Get time entries for hours calculation (still needed for hours tracking)
      let entriesQuery = supabase
        .from('time_entries')
        .select(`
          duration_minutes,
          hourly_rate,
          category,
          project_id,
          projects!inner(id, billing_type, hourly_rate, client_id, fixed_price, status)
        `)
        .eq('user_id', user.id)
        .gte('start_time', filters.startDate.toISOString())
        .lte('start_time', filters.endDate.toISOString())

      if (filters.projectIds?.length) {
        entriesQuery = entriesQuery.in('project_id', filters.projectIds)
      }
      if (filters.categories?.length) {
        entriesQuery = entriesQuery.in('category', filters.categories as any)
      }

      const { data: entries } = await entriesQuery

      // Filter by billing type if specified
      let filteredEntries = entries || []
      if (filters.billingTypes?.length) {
        filteredEntries = filteredEntries.filter((e: any) => 
          filters.billingTypes?.includes(e.projects?.billing_type)
        )
      }
      if (filters.clientIds?.length) {
        filteredEntries = filteredEntries.filter((e: any) =>
          filters.clientIds?.includes(e.projects?.client_id)
        )
      }

      // Calculate hours by type
      let hourlyHours = 0
      let fixedHours = 0

      filteredEntries.forEach((entry: any) => {
        const hours = (entry.duration_minutes || 0) / 60
        
        if (entry.projects?.billing_type === 'hourly') {
          hourlyHours += hours
        } else {
          fixedHours += hours
        }
      })

      // Get project counts (completed projects)
      const { data: completedProjects } = await supabase
        .from('projects')
        .select('id, billing_type, status')
        .eq('user_id', user.id)
        .eq('status', 'done')
        .gte('updated_at', filters.startDate.toISOString())
        .lte('updated_at', filters.endDate.toISOString())

      const hourlyProjectsCompleted = completedProjects?.filter(p => p.billing_type === 'hourly').length || 0
      const fixedProjectsCompleted = completedProjects?.filter(p => p.billing_type === 'fixed').length || 0

      // Get fixed projects effective rate
      const { data: fixedWithHours } = await supabase
        .from('project_revenue')
        .select('effective_hourly_rate')
        .eq('user_id', user.id)
        .eq('billing_type', 'fixed')
        .gt('hours_spent', 0)

      const avgFixedRate = fixedWithHours?.length
        ? fixedWithHours.reduce((acc, p) => acc + (Number(p.effective_hourly_rate) || 0), 0) / fixedWithHours.length
        : 0

      const totalHours = hourlyHours + fixedHours
      const totalRevenue = hourlyRevenue + fixedRevenue

      return {
        totalHours: Math.round(totalHours),
        hourlyHours: Math.round(hourlyHours),
        fixedHours: Math.round(fixedHours),
        totalRevenue: Math.round(totalRevenue * 100) / 100,
        hourlyRevenue: Math.round(hourlyRevenue * 100) / 100,
        fixedRevenue: Math.round(fixedRevenue * 100) / 100,
        avgHourlyRate: hourlyHours > 0 ? Math.round((hourlyRevenue / hourlyHours) * 100) / 100 : 0,
        avgFixedRate: Math.round(avgFixedRate * 100) / 100,
        projectsCompleted: hourlyProjectsCompleted + fixedProjectsCompleted,
        hourlyProjectsCompleted,
        fixedProjectsCompleted,
        targetRate: profile?.hourly_rate || 80,
      }
    },
    enabled: !!user?.id,
  })
}

// Monthly revenue by type (last 12 months) - NOW USING PAYMENTS DATA
export function useMonthlyRevenueByType(filters: ReportFilters) {
  const { user } = useAuth()

  return useQuery({
    queryKey: ['reports', 'monthly-revenue', user?.id, filters],
    queryFn: async () => {
      if (!user?.id) return null

      const months = eachMonthOfInterval({
        start: subMonths(filters.endDate, 11),
        end: filters.endDate,
      })

      const result = await Promise.all(months.map(async (month) => {
        const monthStart = startOfMonth(month)
        const monthEnd = endOfMonth(month)
        const startStr = format(monthStart, 'yyyy-MM-dd')
        const endStr = format(monthEnd, 'yyyy-MM-dd')

        // Get PAID payments for this month
        const { data: payments } = await supabase
          .from('payments')
          .select(`
            amount,
            projects (billing_type)
          `)
          .eq('status', 'paid')
          .gte('paid_date', startStr)
          .lte('paid_date', endStr)

        let hourlyRevenue = 0
        let fixedRevenue = 0
        
        payments?.forEach((payment: any) => {
          const amount = Number(payment.amount) || 0
          if (payment.projects?.billing_type === 'hourly') {
            hourlyRevenue += amount
          } else {
            fixedRevenue += amount
          }
        })

        return {
          month: format(month, 'MMM', { locale: ptBR }),
          fullMonth: format(month, 'MMMM yyyy', { locale: ptBR }),
          hourly: Math.round(hourlyRevenue * 100) / 100,
          fixed: Math.round(fixedRevenue * 100) / 100,
          total: Math.round((hourlyRevenue + fixedRevenue) * 100) / 100,
        }
      }))

      return result
    },
    enabled: !!user?.id,
  })
}

// Revenue distribution (hourly vs fixed pie chart)
export function useRevenueDistribution(filters: ReportFilters) {
  const { data: summary } = useReportsSummary(filters)

  return useQuery({
    queryKey: ['reports', 'distribution', summary],
    queryFn: async () => {
      if (!summary) return null

      const total = summary.hourlyRevenue + summary.fixedRevenue
      if (total === 0) return []

      return [
        {
          name: 'Por Hora',
          value: summary.hourlyRevenue,
          percent: Math.round((summary.hourlyRevenue / total) * 100),
          color: 'hsl(217, 91%, 60%)',
        },
        {
          name: 'Preço Fixo',
          value: summary.fixedRevenue,
          percent: Math.round((summary.fixedRevenue / total) * 100),
          color: 'hsl(142, 71%, 45%)',
        },
      ]
    },
    enabled: !!summary,
  })
}

// Hours by client
export function useHoursByClient(filters: ReportFilters) {
  const { user } = useAuth()

  return useQuery({
    queryKey: ['reports', 'hours-by-client', user?.id, filters],
    queryFn: async () => {
      if (!user?.id) return null

      const { data: entries } = await supabase
        .from('time_entries')
        .select(`
          duration_minutes,
          projects!inner(client_id, clients(id, name))
        `)
        .eq('user_id', user.id)
        .gte('start_time', filters.startDate.toISOString())
        .lte('start_time', filters.endDate.toISOString())

      const hoursByClient: Record<string, { name: string; hours: number }> = {}

      entries?.forEach((entry: any) => {
        const clientId = entry.projects?.client_id || 'no-client'
        const clientName = entry.projects?.clients?.name || 'Sem Cliente'
        
        if (!hoursByClient[clientId]) {
          hoursByClient[clientId] = { name: clientName, hours: 0 }
        }
        hoursByClient[clientId].hours += (entry.duration_minutes || 0) / 60
      })

      const colors = [
        'hsl(217, 91%, 60%)',
        'hsl(142, 71%, 45%)',
        'hsl(38, 92%, 50%)',
        'hsl(0, 84%, 60%)',
        'hsl(280, 84%, 60%)',
        'hsl(180, 70%, 45%)',
      ]

      return Object.values(hoursByClient)
        .map((client, index) => ({
          ...client,
          hours: Math.round(client.hours * 10) / 10,
          color: colors[index % colors.length],
        }))
        .sort((a, b) => b.hours - a.hours)
        .slice(0, 6)
    },
    enabled: !!user?.id,
  })
}

// Fixed projects profitability (scatter chart)
export function useFixedProjectsProfitability(filters: ReportFilters) {
  const { user } = useAuth()
  const { data: profile } = useProfile()

  return useQuery({
    queryKey: ['reports', 'fixed-profitability', user?.id, filters],
    queryFn: async () => {
      if (!user?.id) return null

      const { data } = await supabase
        .from('project_revenue')
        .select('*')
        .eq('user_id', user.id)
        .eq('billing_type', 'fixed')
        .gt('hours_spent', 0)

      const targetRate = profile?.hourly_rate || 80

      return data?.map(p => {
        const rate = Number(p.effective_hourly_rate) || 0
        let color = 'hsl(142, 71%, 45%)' // green
        if (rate < targetRate * 0.9) {
          color = 'hsl(0, 84%, 60%)' // red
        } else if (rate < targetRate * 1.1) {
          color = 'hsl(38, 92%, 50%)' // yellow
        }

        return {
          id: p.id,
          name: p.name,
          hours: Number(p.hours_spent) || 0,
          rate,
          value: Number(p.fixed_price) || 0,
          targetRate,
          color,
          status: p.status,
        }
      }) || []
    },
    enabled: !!user?.id,
  })
}

// Hourly vs Fixed comparison
export function useHourlyVsFixedComparison(filters: ReportFilters) {
  const { data: summary } = useReportsSummary(filters)
  const { data: profitability } = useFixedProjectsProfitability(filters)

  return useQuery({
    queryKey: ['reports', 'comparison', summary, profitability],
    queryFn: async () => {
      if (!summary) return null

      const fixedAvgRate = profitability?.length
        ? profitability.reduce((acc, p) => acc + p.rate, 0) / profitability.length
        : 0

      const hourlyRentability = summary.avgHourlyRate >= summary.targetRate
      const fixedRentability = fixedAvgRate >= summary.targetRate
      const fixedRentabilityPercent = summary.targetRate > 0
        ? Math.round((fixedAvgRate / summary.targetRate) * 100)
        : 0

      return {
        hourly: {
          revenue: summary.hourlyRevenue,
          projects: summary.hourlyProjectsCompleted,
          hours: summary.hourlyHours,
          avgRate: summary.avgHourlyRate,
          revenuePerProject: summary.hourlyProjectsCompleted > 0
            ? Math.round(summary.hourlyRevenue / summary.hourlyProjectsCompleted)
            : 0,
          rentability: hourlyRentability,
          rentabilityPercent: summary.targetRate > 0
            ? Math.round((summary.avgHourlyRate / summary.targetRate) * 100)
            : 0,
        },
        fixed: {
          revenue: summary.fixedRevenue,
          projects: summary.fixedProjectsCompleted,
          hours: summary.fixedHours,
          avgRate: Math.round(fixedAvgRate),
          revenuePerProject: summary.fixedProjectsCompleted > 0
            ? Math.round(summary.fixedRevenue / summary.fixedProjectsCompleted)
            : 0,
          rentability: fixedRentability,
          rentabilityPercent: fixedRentabilityPercent,
        },
        insights: generateInsights(summary, fixedAvgRate),
      }
    },
    enabled: !!summary,
  })
}

function generateInsights(summary: any, fixedAvgRate: number): string[] {
  const insights: string[] = []

  if (fixedAvgRate > summary.avgHourlyRate * 1.1 && fixedAvgRate > 0) {
    const diff = Math.round((fixedAvgRate / summary.avgHourlyRate - 1) * 100)
    insights.push(`Seus projetos fixos são ${diff}% mais rentáveis`)
  }

  if (summary.avgHourlyRate < summary.targetRate && summary.hourlyHours > 0) {
    insights.push(`Taxa média por hora abaixo da meta, considere ajustar preços`)
  }

  if (summary.fixedRevenue > summary.hourlyRevenue && summary.hourlyRevenue > 0) {
    const percent = Math.round((summary.fixedRevenue / (summary.fixedRevenue + summary.hourlyRevenue)) * 100)
    insights.push(`Projetos fixos representam ${percent}% da sua receita`)
  }

  return insights
}

// Weekly heatmap data
export function useWeeklyHeatmap(filters: ReportFilters) {
  const { user } = useAuth()

  return useQuery({
    queryKey: ['reports', 'heatmap', user?.id, filters],
    queryFn: async () => {
      if (!user?.id) return null

      const { data: entries } = await supabase
        .from('time_entries')
        .select('start_time, duration_minutes')
        .eq('user_id', user.id)
        .gte('start_time', filters.startDate.toISOString())
        .lte('start_time', filters.endDate.toISOString())

      const weekDays = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb']
      const hours = Array.from({ length: 24 }, (_, i) => i)

      // Initialize grid
      const grid: Record<string, Record<number, number>> = {}
      weekDays.forEach(day => {
        grid[day] = {}
        hours.forEach(hour => {
          grid[day][hour] = 0
        })
      })

      // Fill with data
      entries?.forEach(entry => {
        const date = new Date(entry.start_time)
        const dayIndex = getDay(date)
        const hour = getHours(date)
        grid[weekDays[dayIndex]][hour] += (entry.duration_minutes || 0) / 60
      })

      // Convert to array format
      const result: Array<{ day: string; hour: number; value: number }> = []
      weekDays.forEach(day => {
        hours.forEach(hour => {
          result.push({
            day,
            hour,
            value: Math.round(grid[day][hour] * 10) / 10,
          })
        })
      })

      return result
    },
    enabled: !!user?.id,
  })
}

// Projects table data
export function useProjectsTableData(filters: ReportFilters) {
  const { user } = useAuth()
  const { data: profile } = useProfile()

  return useQuery({
    queryKey: ['reports', 'projects-table', user?.id, filters],
    queryFn: async () => {
      if (!user?.id) return null

      const { data: projects } = await supabase
        .from('project_revenue')
        .select('*')
        .eq('user_id', user.id)

      // Get client names
      const { data: projectsWithClients } = await supabase
        .from('projects')
        .select('id, clients(name)')
        .eq('user_id', user.id)

      const clientMap: Record<string, string> = {}
      projectsWithClients?.forEach((p: any) => {
        clientMap[p.id] = p.clients?.name || 'Sem Cliente'
      })

      const targetRate = profile?.hourly_rate || 80

      return projects?.map(p => ({
        id: p.id,
        name: p.name,
        client: clientMap[p.id || ''] || 'Sem Cliente',
        billingType: p.billing_type,
        status: p.status,
        hoursSpent: Number(p.hours_spent) || 0,
        rate: p.billing_type === 'hourly' 
          ? (Number(p.effective_hourly_rate) || 0)
          : (Number(p.effective_hourly_rate) || 0),
        revenue: Number(p.revenue) || 0,
        rentabilityPercent: targetRate > 0
          ? Math.round(((Number(p.effective_hourly_rate) || 0) / targetRate) * 100)
          : 0,
      })) || []
    },
    enabled: !!user?.id,
  })
}

// Clients for filter dropdown
export function useClientsForFilter() {
  const { user } = useAuth()

  return useQuery({
    queryKey: ['reports', 'clients-filter', user?.id],
    queryFn: async () => {
      if (!user?.id) return []

      const { data } = await supabase
        .from('clients')
        .select('id, name')
        .eq('user_id', user.id)
        .order('name')

      return data || []
    },
    enabled: !!user?.id,
  })
}

// Projects for filter dropdown
export function useProjectsForFilter() {
  const { user } = useAuth()

  return useQuery({
    queryKey: ['reports', 'projects-filter', user?.id],
    queryFn: async () => {
      if (!user?.id) return []

      const { data } = await supabase
        .from('projects')
        .select('id, name')
        .eq('user_id', user.id)
        .order('name')

      return data || []
    },
    enabled: !!user?.id,
  })
}
